import { useState, useEffect, useCallback } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from './components/Dropdown';
import { DownloadIcon } from './components/Icons';
import { ARCGIS_ROOT_URL, DOWNLOAD_FILE_TYPES, APP_SETTINGS } from './settings';
import { useEsri } from './EsriContext';
import { useAppState } from './AppContext';
import { toast } from 'react-toastify';
import { SingleRowPlaceholder } from './components/ListRow';

const { GIS_SERVER } = APP_SETTINGS;
const { GP } = GIS_SERVER;

const DownloadLink = ({ url, fileTypeName }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="inline-block font-medium underline"
  >
    Click here to download the file ({fileTypeName})
  </a>
);

const getFileTypeName = (fileType) => {
  return DOWNLOAD_FILE_TYPES.filter((file) => file.value === fileType)[0];
};

const Download = () => {
  const [selectFileType, setSelectFileType] = useState(null);
  const [appState] = useAppState();
  const { geoProcessor, getToken } = useEsri();

  const updateDownloadFileUrl = useCallback(
    (url = '') => {
      const { token } = getToken();
      const { pathname } = new URL(url);

      return `${ARCGIS_ROOT_URL}${pathname}/data?token=${token}`;
    },
    [getToken]
  );

  useEffect(() => {
    if (!selectFileType) return;

    const { name: fileName } = getFileTypeName(selectFileType);
    const toastItem = toast.loading(`Preparing to Download ${fileName}..`);

    (async function () {
      try {
        const gpDownload = await geoProcessor(GP.DOWNLOAD_LAYER_URL);
        const { url } = appState.download_layer_info;
        const params = {
          inputLayers: [{ url: url + `/0` }],
          clip: false,
          extent: '',
          dataFormat: selectFileType,
        };

        const { jobId } = await gpDownload.submitJob(params);
        const GPwaitForJobCompletion = await gpDownload.waitForJobCompletion(
          jobId
        );

        if (GPwaitForJobCompletion.jobStatus === 'job-succeeded') {
          const { value } = await gpDownload.getResultData(
            GPwaitForJobCompletion.jobId,
            'contentID'
          );
          const urlWithToken = updateDownloadFileUrl(value?.url);

          toast.update(toastItem, {
            render: <DownloadLink url={urlWithToken} fileTypeName={fileName} />,
            type: 'success',
            isLoading: false,
          });
        }
      } catch (e) {
        toast.update(toastItem, {
          render: () => `Try again.`,
          type: 'error',
          isLoading: false,
        });
      }
    })();
  }, [
    appState.download_layer_info,
    geoProcessor,
    getToken,
    selectFileType,
    updateDownloadFileUrl,
  ]);

  return (
    <div className="text-sm p-4 mb-8 bg-blue-700 rounded">
      <p className="w-full text-grey-darkest pb-4 font-bold">
        Download Smoke Control Areas and Exemptions Layer
      </p>

      {!appState.download_layer_info ? (
        <SingleRowPlaceholder />
      ) : (
        <Dropdown testId="data_download-test">
          <DropdownToggle>
            <span className="ml-1 mr-8 hidden md:inline-block m-auto">
              Select File Type
            </span>
            <span className="mr-1 ml-1">
              <DownloadIcon />
            </span>
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </DropdownToggle>
          <DropdownMenu>
            {DOWNLOAD_FILE_TYPES.map((menuItem) => (
              <DropdownItem
                key={menuItem.value}
                value={menuItem.value}
                onSelect={(value) => {
                  setSelectFileType(value);
                }}
              >
                {menuItem.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      )}
    </div>
  );
};

export default Download;
